import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { formatCategory } from '../utils/formatCategory';
import { getCustomImageUrl } from '../utils/imageProxy';

interface EditorialProjectCardProps {
  id: string;
  slug?: string;
  title: string;
  description: string;
  category: string;
  image?: string;
  index: number;
}

export function EditorialProjectCard({ 
  id, 
  slug,
  title, 
  description, 
  category, 
  image,
  index 
}: EditorialProjectCardProps) {
  const isReversed = index % 2 === 1;

  return (
    <motion.article 
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: 0.1 }}
      className="group cursor-pointer"
    >
      <Link to={`/project/${slug || id}`}>
        <div className={`grid md:grid-cols-12 gap-8 items-center ${isReversed ? '' : ''}`}>
          {/* Image */}
          <div className={`md:col-span-7 ${isReversed ? 'md:order-2' : 'md:order-1'}`}>
            <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-lg overflow-hidden">
              {image ? (
                <img 
                  src={getCustomImageUrl(image, { width: 800, quality: 85, format: 'webp' })} 
                  alt={title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-6xl opacity-20">{category === 'Development' ? '💻' : category === 'Design' ? '🎨' : '📊'}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Content */}
          <div className={`md:col-span-5 space-y-4 ${isReversed ? 'md:order-1' : 'md:order-2'}`}>
            <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
              {formatCategory(category)}
            </p>
            <h3 className="text-3xl font-light text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">
              {title}
            </h3>
            <div 
              className="text-gray-600 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: description || 'Project description' 
              }}
            />
            <div className="inline-flex items-center gap-2 text-gray-900 dark:text-white font-medium group-hover:gap-4 transition-all duration-300">
              View Project Details
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
